import React, {
  useEffect,
} from 'react';

import {
  useDispatch,
  useSelector,
} from 'react-redux';

import AppRoutes from './routes';

import 'bootstrap/dist/css/bootstrap.min.css';
import './App.css';
import './assets/styles/fonts.css';

import {
  I18nextProvider,
} from 'react-i18next';

import i18n from './i18n';
import normalizeLocale from './utils/i18n-utils';

import API from './api/axios';
import {
  getUserInfo,
} from './auth/authService';

import {
  updateAccessToken,
  logout,
  setUser,
  setAllCompanies,
  setCompanyID,
  setBootstrapped,
} from './features/auth/authSlice';

import {
  resolveBootstrapCompanyID,
} from './utils/resolveCompanyContext';

function App() {
  const dispatch = useDispatch();

  /**
   * App 冷启动恢复会话。
   *
   * 执行顺序：
   * 1. refresh cookie 换 accessToken
   * 2. 请求 /users/me
   * 3. 恢复 user 和 allCompanies
   * 4. 恢复 companyID
   * 5. 完成 bootstrapped
   */
  useEffect(() => {
    let cancelled = false;

    const bootstrapAuth =
      async () => {
        try {
          const { data } =
            await API.post(
              '/auth/refresh',
            );

          const token =
            data?.accessToken ||
            null;

          if (!token) {
            if (!cancelled) {
              dispatch(logout());
            }

            return;
          }

          if (!cancelled) {
            dispatch(
              updateAccessToken(
                token,
              ),
            );
          }

          const me =
            await getUserInfo();

          if (!me) {
            throw new Error(
              'Unable to load current user',
            );
          }

          if (cancelled) {
            return;
          }

          dispatch(
            setUser(me),
          );

          dispatch(
            setAllCompanies(
              me.allCompanies ||
              [],
            ),
          );

          const bootstrapCompanyID =
            resolveBootstrapCompanyID({
              user: me,
              search:
                window.location
                  .search,
            });

          dispatch(
            setCompanyID(
              bootstrapCompanyID,
            ),
          );
        } catch (error) {
          console.error(
            'bootstrap refresh failed:',
            {
              status:
                error?.response
                  ?.status,

              data:
                error?.response
                  ?.data,

              message:
                error?.message,
            },
          );

          if (!cancelled) {
            dispatch(logout());
          }
        } finally {
          if (!cancelled) {
            dispatch(
              setBootstrapped(
                true,
              ),
            );
          }
        }
      };

    bootstrapAuth();

    return () => {
      cancelled = true;
    };

    // 冷启动只执行一次。
    // companyId 的后续变化由 Topbar 和 useCompanyContext 管理。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  /**
   * 多标签登出同步。
   */
  useEffect(() => {
    let broadcastChannel;

    try {
      broadcastChannel =
        new BroadcastChannel(
          'auth',
        );

      broadcastChannel.onmessage =
        (event) => {
          if (
            event.data?.type ===
            'LOGOUT'
          ) {
            dispatch(logout());
          }
        };
    } catch {
      // 浏览器不支持 BroadcastChannel 时忽略。
    }

    return () => {
      try {
        broadcastChannel?.close();
      } catch {
        // 无需处理关闭异常。
      }
    };
  }, [dispatch]);

  /**
   * 语言恢复逻辑。
   */
  const lang = useSelector(
    (state) =>
      state.language.lang,
  );

  useEffect(() => {
    const target =
      normalizeLocale(lang);

    if (
      i18n.language !== target
    ) {
      i18n.changeLanguage(
        target,
      );
    }
  }, [lang]);

  return (
    <I18nextProvider
      i18n={i18n}
    >
      <AppRoutes
        style={{
          fontFamily:
            'Graphik',
        }}
      />
    </I18nextProvider>
  );
}

export default App;